import { differenceInCalendarDays } from 'date-fns';
import { parseCalendarDate } from '@/lib/dates';

export const NOT_A_TEAM = 'Not a team block';
export const isPartialPickup = (row: { block_count: number; pickup_pct: number }) =>
  Number(row.block_count) > 0 && Number(row.pickup_pct) < 100;

export function releaseDays(value: string | null | undefined, today = new Date()) {
  const date = parseCalendarDate(value);
  return date ? differenceInCalendarDays(date, today) : null;
}

export function isReleaseSoon(value: string | null | undefined, today = new Date()) {
  const days = releaseDays(value, today);
  return days !== null && days >= 0 && days <= 7;
}

export function isUnlinkedBlock(block: { registration_id: string | null; notes: string | null }) {
  return !block.registration_id && block.notes !== NOT_A_TEAM;
}

export const blockPickupPct = (held: number, reserved: number) => held > 0 ? Math.round(reserved / held * 100) : 0;

export function blockTotals(blocks: { hotel_property_id: string | null; rooms_held: number; rooms_reserved: number }[], propertyId: string) {
  return blocks.filter(b => b.hotel_property_id === propertyId).reduce((sum, b) => ({
    held: sum.held + Number(b.rooms_held || 0), reserved: sum.reserved + Number(b.rooms_reserved || 0),
  }), { held: 0, reserved: 0 });
}

// --- Staybook "Client Reporting Dashboard" pickup report ---

export const STAYBOOK_HEADERS = ['Age Group', 'Team Name', 'Property Name', 'Reservations Booked', 'Room Nights Booked', 'Room Nights On Hold'];

// Staybook exports carry a BOM on the first header and non-breaking spaces in cells.
export const cleanCell = (value: string | null | undefined) =>
  String(value ?? '').replace(/\uFEFF/g, '').replace(/\u00A0/g, ' ').trim();

export function normalizeHeaders(fields: string[] | undefined) {
  return (fields || []).map(f => cleanCell(f));
}

export interface StaybookRow {
  ageGroup: string;
  teamName: string;
  propertyName: string;
  reservationsBooked: number;
  roomNightsBooked: number;
  roomNightsOnHold: number;
}

const toCount = (value: string | null | undefined) => {
  const n = Number(cleanCell(value));
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
};

export function parseStaybookRow(raw: Record<string, string>): StaybookRow | null {
  const row: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) row[cleanCell(key)] = value;
  const teamName = cleanCell(row['Team Name']);
  const propertyName = cleanCell(row['Property Name']);
  if (!teamName || !propertyName) return null; // summary/blank lines
  return {
    ageGroup: cleanCell(row['Age Group']),
    teamName,
    propertyName,
    reservationsBooked: toCount(row['Reservations Booked']),
    roomNightsBooked: toCount(row['Room Nights Booked']),
    roomNightsOnHold: toCount(row['Room Nights On Hold']),
  };
}

const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// Synthetic block code keeps re-uploads idempotent (Staybook rows have no block code).
// Keyed by hotel + team + age group: the report has one row per team per age group at a hotel.
export const staybookBlockCode = (row: StaybookRow) =>
  `STAYBOOK-${slug(row.propertyName)}-${slug(row.teamName)}-${slug(row.ageGroup) || 'na'}`.slice(0, 120);

// Rows that slug to the same code (e.g. "17U , 15U" vs "17U, 15U") get a stable numeric
// suffix so every report row gets its own block; identical files re-upload in the same order.
export function uniqueStaybookCodes(rows: StaybookRow[]) {
  const seen: Record<string, number> = {};
  return rows.map(row => {
    const base = staybookBlockCode(row);
    const n = (seen[base] = (seen[base] || 0) + 1);
    return n === 1 ? base : `${base.slice(0, 116)}-${n}`;
  });
}

// Staybook pickup rows carry room nights, not room counts; sum them per team for the tracker.
export function staybookNightsByTeam(blocks: { registration_id: string | null; rooms_held: number; room_nights_booked: number | null }[]) {
  const map: Record<string, number> = {};
  for (const b of blocks || []) {
    if (!b.registration_id || Number(b.rooms_held || 0) > 0) continue;
    map[b.registration_id] = (map[b.registration_id] || 0) + Number(b.room_nights_booked || 0);
  }
  return map;
}

export const matchByName = <T extends Record<string, string>>(list: T[] | undefined, field: keyof T, name: string) =>
  (list || []).find(item => cleanCell(item[field]).toLowerCase() === name.toLowerCase());